import {
  browserAccountStateSessionRepository,
} from "../js/account/account-state-session-repository.js";
import {
  readTrackerBetaAccessV2,
} from "../js/account/tracker-beta-access.js";

const TRACKER_ACCESS_PATH = "/api/v1/account/tracker-beta";

function hasMethods(value, methods) {
  return Boolean(value) && methods.every((method) => typeof value[method] === "function");
}

export function createCharacterStreamAccessController({
  sessionRepository,
  readAccess,
  mountStream,
  unmountStream,
  onAccessChange = () => {},
} = {}) {
  if (!hasMethods(sessionRepository, ["load", "subscribe"])) {
    throw new TypeError("Character stream access requires the account-session owner.");
  }
  if (
    typeof readAccess !== "function"
    || typeof mountStream !== "function"
    || typeof unmountStream !== "function"
    || typeof onAccessChange !== "function"
  ) {
    throw new TypeError("Character stream access requires lifecycle callbacks.");
  }

  let started = false;
  let stopped = false;
  let unsubscribe = null;
  let request = null;
  let requestGeneration = 0;
  let accountId = null;
  let streamMounted = false;
  let accessState = Object.freeze({ accountReady: false, trackerEnabled: false });

  function publishAccess(accountReady, trackerEnabled) {
    if (
      accessState.accountReady === accountReady
      && accessState.trackerEnabled === trackerEnabled
    ) {
      return false;
    }
    accessState = Object.freeze({ accountReady, trackerEnabled });
    onAccessChange(accessState);
    return true;
  }

  function cancelRequest() {
    requestGeneration += 1;
    request?.abort();
    request = null;
  }

  function removeStream() {
    if (!streamMounted) return false;
    streamMounted = false;
    unmountStream();
    return true;
  }

  function closeAccess({ accountReady = false } = {}) {
    cancelRequest();
    removeStream();
    publishAccess(accountReady, false);
  }

  async function loadAccess(session) {
    cancelRequest();
    const generation = requestGeneration;
    const controller = new AbortController();
    request = controller;
    publishAccess(true, false);
    try {
      const result = await readAccess(session, { signal: controller.signal });
      if (
        stopped
        || controller.signal.aborted
        || generation !== requestGeneration
        || accountId !== session.accountId
      ) {
        return;
      }
      if (result?.kind !== "enabled") {
        publishAccess(result?.kind === "disabled", false);
        return;
      }
      streamMounted = true;
      try {
        mountStream();
      } catch (error) {
        removeStream();
        throw error;
      }
      publishAccess(true, true);
    } catch (error) {
      if (!controller.signal.aborted && error?.name !== "AbortError") {
        publishAccess(true, false);
      }
    } finally {
      if (request === controller) request = null;
    }
  }

  function sessionChanged(session) {
    if (stopped || session?.kind !== "ready") {
      accountId = null;
      closeAccess();
      return;
    }
    if (accountId === session.accountId) return;
    accountId = session.accountId;
    closeAccess({ accountReady: true });
    void loadAccess(session);
  }

  function start() {
    if (started || stopped) return false;
    started = true;
    unsubscribe = sessionRepository.subscribe(sessionChanged);
    void sessionRepository.load();
    return true;
  }

  function stop() {
    if (stopped) return false;
    stopped = true;
    unsubscribe?.();
    unsubscribe = null;
    accountId = null;
    closeAccess();
    return true;
  }

  return Object.freeze({
    accessState: () => accessState,
    start,
    stop,
  });
}

export function createBrowserCharacterStreamAccessController({
  shell,
  onAccessChange,
  onStreamMounted,
  onStreamUnmounted,
  sessionRepository = browserAccountStateSessionRepository(),
  readAccess = (_session, { signal }) => readTrackerBetaAccessV2({
    url: globalThis.window?.__fishystuffResolveApiUrl?.(TRACKER_ACCESS_PATH),
    signal,
  }),
} = {}) {
  const mount = shell?.querySelector?.("[data-map-character-stream-mount]");
  const template = shell?.querySelector?.("template[data-map-character-stream-template]");
  if (!mount || !template?.content?.cloneNode) {
    throw new TypeError("Character stream access requires its inert map template.");
  }
  if (
    typeof onStreamMounted !== "function"
    || typeof onStreamUnmounted !== "function"
  ) {
    throw new TypeError("Character stream access requires stream lifecycle callbacks.");
  }

  let streamElement = null;
  return createCharacterStreamAccessController({
    sessionRepository,
    readAccess,
    onAccessChange,
    mountStream() {
      mount.replaceChildren(template.content.cloneNode(true));
      streamElement = mount.querySelector?.("[data-map-character-stream]") ?? null;
      if (!streamElement) {
        mount.replaceChildren();
        throw new TypeError("Character stream template did not create its request element.");
      }
      onStreamMounted(streamElement);
    },
    unmountStream() {
      const removed = streamElement;
      streamElement = null;
      onStreamUnmounted(removed);
      mount.replaceChildren();
    },
  });
}
